import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Headphones, PhoneOff, X } from 'lucide-react';

import { useCheckTestCallNumber } from '../../../services/onboardingBoard';
import { useCallCenter } from './callCenter/CallCenterProvider';
import { formatPhoneToken } from './boardUtils';

// "Test call audio" — the settings-menu self-test (Nic 2026-08-30, after a
// specialist couldn't hear outbound ringing and had no way to check without
// calling a real lead). Types their OWN number, the server vets it (a lead or
// client match is refused: off-book calls, plus the RC call-detection
// webhooks match by phone and would stamp false contact evidence), then the
// call goes out through the exact same webphone path as a real call — same
// audio elements, same ringback wiring, same hangup.

// Mirrors normalizePhoneInput in NotificationSettingsPage: 10 digits or
// 11-with-leading-1, anything else is not a dialable US number.
const normalizePhoneInput = (raw) => {
  const d = String(raw || '').replace(/\D/g, '');
  if (d.length === 10) return `+1${d}`;
  if (d.length === 11 && d.startsWith('1')) return `+${d}`;
  return null;
};

const TestAudioModal = ({ show, onHide }) => {
  const { testCall, startTestCall, endTestCall, clearTestCall } =
    useCallCenter();

  const [phone, setPhone] = useState('');
  const [checkError, setCheckError] = useState(null);

  const checkMut = useCheckTestCallNumber(null, {
    onError: (err) =>
      setCheckError(
        err?.response?.data?.msg || 'The number could not be checked. Try again.',
      ),
  });

  const valid = normalizePhoneInput(phone);
  const calling =
    testCall?.status === 'dialing' || testCall?.status === 'live';

  // BoardPage owns this modal, so navigating off the board unmounts it
  // WITHOUT close() — never leave a live test call running with no UI.
  const callingRef = useRef(calling);
  callingRef.current = calling;
  useEffect(
    () => () => {
      if (callingRef.current) {
        endTestCall();
        clearTestCall();
      }
    },
    // Stable provider callbacks; the cleanup only ever runs on unmount.
    [endTestCall, clearTestCall],
  );

  const runTest = async () => {
    if (!valid || checkMut.isLoading || calling) return;
    setCheckError(null);
    let vetted;
    try {
      vetted = await checkMut.mutateAsync({ phone });
    } catch (_) {
      return; // onError already surfaced the copy
    }
    await startTestCall(vetted?.e164 || valid);
  };

  // A test call never outlives its modal: closing (X or backdrop) ends a
  // live call — an invisible live call is exactly the confusion this
  // feature exists to debug.
  const close = () => {
    if (calling) endTestCall();
    clearTestCall();
    setCheckError(null);
    setPhone('');
    onHide();
  };

  if (!show) return null;

  return createPortal(
    <div className="fixed inset-0 z-[1080] flex items-end justify-center sm:items-center">
      <div
        className="absolute inset-0 bg-slate-900/30 dark:bg-black/60"
        aria-hidden
        onClick={close}
      />
      <div className="relative m-4 w-full max-w-md rounded-2xl bg-white p-4 shadow-xl dark:!bg-surface-elevated dark:ring-1 dark:ring-white/10">
        <div className="mb-1 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-bold text-slate-800 dark:text-slate-100">
            <Headphones size={15} className="text-slate-400" />
            Test call audio
          </div>
          <button
            type="button"
            onClick={close}
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-white/10"
          >
            <X size={14} />
          </button>
        </div>
        <div className="mb-3 text-xs text-slate-400">
          Make a test call through the webphone to test your audio.
        </div>

        {calling ? (
          <div className="mb-3 flex items-center justify-between rounded-lg bg-slate-50 p-3 dark:bg-white/5">
            <div className="flex items-center gap-2.5">
              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-[0.75]" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
              </span>
              <div>
                <div className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                  Calling {formatPhoneToken(testCall.number)}
                </div>
                <div className="text-[11px] text-slate-400">
                  Listen for ringing in your headset.
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={endTestCall}
              className="flex items-center gap-1.5 rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-500"
            >
              <PhoneOff size={12} />
              End call
            </button>
          </div>
        ) : testCall?.status === 'failed' ? (
          <div className="mb-3 rounded-lg bg-rose-50 p-3 text-xs text-rose-600 dark:bg-rose-500/10 dark:text-rose-300">
            The call could not be started. Check the RingCentral chip in the
            board header and your browser's microphone permission, then try
            again.
          </div>
        ) : (
          <label className="mb-3 block">
            <span className="mb-1 block text-[11px] font-semibold text-slate-500 dark:text-slate-400">
              Your personal phone number
            </span>
            <input
              type="tel"
              value={phone}
              placeholder="(555) 123-4567"
              onChange={(e) => {
                setPhone(e.target.value);
                setCheckError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') runTest();
              }}
              className="w-full rounded-lg border border-slate-200 bg-transparent px-2.5 py-1.5 text-xs text-slate-700 focus:border-slate-400 focus:outline-none dark:border-white/10 dark:text-slate-200 dark:focus:border-white/30"
            />
          </label>
        )}

        {checkError ? (
          <div className="mb-3 text-[11px] font-medium text-rose-500 dark:text-rose-300">
            {checkError}
          </div>
        ) : null}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={close}
            className="rounded-lg px-3 py-1.5 text-xs font-medium text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-white/10"
          >
            Close
          </button>
          {testCall?.status === 'failed' ? (
            <button
              type="button"
              onClick={() => {
                clearTestCall();
                setCheckError(null);
              }}
              className="rounded-lg bg-slate-900 px-3.5 py-1.5 text-xs font-semibold text-white hover:bg-slate-700 dark:bg-slate-200 dark:!text-slate-900 dark:hover:bg-white"
            >
              Test again
            </button>
          ) : !calling ? (
            <button
              type="button"
              disabled={!valid || checkMut.isLoading}
              onClick={runTest}
              className="rounded-lg bg-slate-900 px-3.5 py-1.5 text-xs font-semibold text-white hover:bg-slate-700 disabled:opacity-50 dark:bg-slate-200 dark:!text-slate-900 dark:hover:bg-white"
            >
              {checkMut.isLoading ? 'Checking…' : 'Test Audio'}
            </button>
          ) : null}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default TestAudioModal;
